import { useState } from 'react'
import { capitalize } from 'lodash'
import {
  BadgeCheck,
  CheckCircle,
  CircleAlert,
  ClockAlert,
  Edit,
  FileText,
  Info,
  ReceiptText,
  Trash2,
  UploadCloud,
} from 'lucide-react'
import { useDownloadCashReceiptMutation } from '../hooks'
import { DeleteDonation } from './DeleteDonation'
import { DonationForm } from './DonationForm'
import { DonationDetailDialog } from './DonationDetailDialog'
import { UploadDepositReceiptForm } from './UploadDepositReceiptForm'
import type { Donation } from '@/features/donations/types/donation'
import type { PermissionHelper } from '@/utils/permissions'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/custom/modal'
import { formatCurrency } from '@/utils/currencyUtils'
import { formatDate } from '@/utils/timeZoneUtils'
import ExportDialog from '@/features/receipts/components/ExportDialog'

interface DonationListCardProps {
  donation: Donation
  donorName: string
  churchName: string
  permissions: PermissionHelper
  open: {
    edit: boolean
    delete: boolean
    pending: boolean
    detail: boolean
  }
  donationId: string | null
  onEdit: (donationId: string) => void
  onDelete: (donationId: string) => void
  onPending: (donationId: string) => void
  onDetail: (donationId: string) => void
  onClose: () => void
}

export function DonationListCard({
  donation,
  donorName,
  churchName,
  permissions,
  open,
  donationId,
  onEdit,
  onDelete,
  onPending,
  onDetail,
  onClose,
}: DonationListCardProps) {
  const [viewReceipt, setViewReceipt] = useState(false)
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null)

  const {
    mutateAsync: downloadReceiptMutation,
    isPending: downloadingReceipt,
  } = useDownloadCashReceiptMutation()

  const handleViewReceipt = async () => {
    try {
      const result = await downloadReceiptMutation(donation._id)
      if (result.downloadUrl) {
        setReceiptUrl(result.downloadUrl)
        setViewReceipt(true)
      }
    } catch (error) {
      console.error('Failed to get receipt URL:', error)
    }
  }

  const getStatusColor = () => {
    if (donation.status === 'verified') return 'green'
    if (!donation.churchId || !donation.donorId) return 'yellow'
    return 'blue'
  }

  const getStatusIcon = () => {
    return donation.status === 'verified' ? BadgeCheck : ClockAlert
  }

  return (
    <div className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-lg">
          {formatCurrency(donation.amount)}
        </div>
        <div className="flex items-center gap-2">
          <Badge color={getStatusColor()} icon={getStatusIcon()}>
            {capitalize(donation.status)}
          </Badge>
          {donation.method === 'cash' && !donation.deposited && (
            <Badge color="red" icon={CircleAlert}>
              Not Deposited
            </Badge>
          )}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="text-sm">
          <span className="font-medium text-gray-600">Receipt #: </span>
          <span>{donation.receiptNumber}</span>
        </div>
        <div className="text-sm">
          <span className="font-medium text-gray-600">Date: </span>
          <span>{formatDate(donation.date, 'MMM D, YYYY')}</span>
        </div>
        <div className="text-sm">
          <span className="font-medium text-gray-600">Donor: </span>
          <span>{donorName}</span>
        </div>
        <div className="text-sm">
          <span className="font-medium text-gray-600">Church: </span>
          <span>{churchName}</span>
        </div>
        <div className="text-sm">
          <span className="font-medium text-gray-600">Method: </span>
          <span>{capitalize(donation.method)}</span>
        </div>
        {donation.donationRef && (
          <div className="text-sm">
            <span className="font-medium text-gray-600">Reference #: </span>
            <span>{donation.donationRef}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        {donation.status === 'verified' &&
          !donation.isRegistrationFee &&
          permissions.canSendReceipt() && (
            <ExportDialog
              id={donation._id}
              type="donation"
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <FileText className="h-4 w-4 sm:mr-2" />{' '}
                  {`${!donation.sentToMember ? 'Send' : 'Resend'} Receipt`}
                </Button>
              }
            />
          )}

        {/* Details Button - only show for pastors */}
        {permissions.isPastor() && (
          <DonationDetailDialog
            donationId={donation._id}
            onClose={onClose}
            open={open.detail && donationId === donation._id}
            trigger={
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => onDetail(donation._id)}
              >
                <Info className="h-4 w-4 sm:mr-2" /> Details
              </Button>
            }
          />
        )}

        {donation.status === 'pending' &&
          (permissions.isSystemAdmin() ||
            (donation.churchId &&
              permissions.canVerifyDonation(donation.churchId))) && (
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => onPending(donation._id)}
            >
              <CheckCircle className="h-4 w-4 sm:mr-2" /> Verify
            </Button>
          )}

        {donation.status === 'verified' &&
          donation.churchId &&
          permissions.canEditDonation(donation.churchId) && (
            <DonationForm
              id={donation._id}
              onClose={onClose}
              open={open.edit && donationId === donation._id}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => onEdit(donation._id)}
                >
                  <Edit className="h-4 w-4 sm:mr-2" /> Edit
                </Button>
              }
              type="edit"
            />
          )}

        {/* Cash donation receipt actions */}
        {donation.method === 'cash' &&
          donation.churchId &&
          permissions.canEditDonation(donation.churchId) && (
            <>
              <UploadDepositReceiptForm
                donationId={donation._id}
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    <UploadCloud className="h-4 w-4 sm:mr-2" />
                    {donation.deposited
                      ? 'Replace Deposit Receipt'
                      : 'Upload Deposit Receipt'}
                  </Button>
                }
              />
              {donation.deposited && donation.depositReceiptDrivePath && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={handleViewReceipt}
                  disabled={downloadingReceipt}
                >
                  <ReceiptText className="h-4 w-4 sm:mr-2" />
                  {downloadingReceipt ? 'Loading...' : 'View Deposit Receipt'}
                </Button>
              )}
            </>
          )}

        {!donation.sentToMember &&
          donation.churchId &&
          permissions.canDeleteDonation() && (
            <DeleteDonation
              id={donation._id}
              onClose={onClose}
              open={open.delete && donationId === donation._id}
              trigger={
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => onDelete(donation._id)}
                >
                  <Trash2 className="h-4 w-4 sm:mr-2" /> Delete
                </Button>
              }
            />
          )}
      </div>

      {/* Receipt Viewer Modal */}
      {viewReceipt && receiptUrl && (
        <Modal
          open={viewReceipt}
          onClose={() => {
            setViewReceipt(false)
            setReceiptUrl(null)
          }}
          title="Deposit Receipt"
          contentClassName="w-full max-w-[100vw] h-[80vh]"
        >
          <div className="w-full h-full bg-neutral-100 rounded-md overflow-hidden min-h-[50vh] overflow-x-hidden">
            <object
              data={receiptUrl}
              className="w-full h-full border-0"
              aria-label="Receipt Viewer"
              style={{ maxWidth: '100%' }}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}
